import { fetchOrganizations } from 'admin/actions/organization';
import { getUsers } from 'admin/actions/user';
import { Button } from 'admin/components/atoms/Buttons';
import ConfirmModal from 'admin/components/atoms/ConfirmModal';
import Select from 'admin/components/atoms/Select';
import TextArea from 'admin/components/atoms/TextArea';
import TextBox from 'admin/components/atoms/TextBox';
import ApiResults from 'admin/components/molecules/ApiResults';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import usePartnerClient from 'admin/hooks/usePartnerClient';
import { toQueryParams } from 'admin/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Grid from '../../atoms/Grid';
import Headings from '../../atoms/Headings';
import Label from '../../atoms/Label';
import Required from '../../atoms/Required';
import PartnerClientConfirmModal from './PartnerClientConfirmModal';
import { usePartnerClientForm } from './usePartnerClientForm';

const FormWrapper = styled.div``;

const PageTitle = styled(Headings.h2)`
  ${({ theme }) => css`
    ${theme.typography.heading3.bold}
    color: ${theme.colors.gray[300]};
  `}
`;

const FormGrid = styled(Grid.Container)`
  ${Label} {
    margin-bottom: 0;
  }

  ${Select} {
    width: 280px;
  }

  ${TextBox} {
    width: 568px;
  }
  ${TextArea} {
    width: 568px;
    resize: both;
  }

  ${Grid.Divider} {
    margin: 12px 0;
  }
`;

const CreatePartnerClient = ({ history }) => {
  const crumbs = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: 'サービス管理', href: '', isCurrentPage: true },
    { value: 'PartnerClient', href: '/service-settings/partner-clients', isCurrentPage: false },
    { value: 'PartnerClient登録', href: '', isCurrentPage: true },
  ];

  const { partnerClient, initialValues, submitCreate } = usePartnerClient();
  const { form, setForm, handleChangeForm } = usePartnerClientForm(partnerClient);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiResult, setApiResult] = useState(null);

  const nameField = useRef();

  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const clear = () => {
    setForm(initialValues);
    nameField.current.value = '';
  };

  const onSuccess = () => {
    setApiResult({ text: 'PartnerClientは正常に作成されました', isError: false });
    clear();
  };

  const registerPartnerClient = () => {
    const bodyParams = {
      name: form.name,
      admin_user_id: form.admin_user.value.id,
      description: form.description,
    };

    submitCreate(bodyParams, onSuccess);

    closeModal();

    history.push('/service-settings/partner-clients');
  };

  useEffect(() => {
    fetchOrganizations('per_page=10000').then((resOrgs) => {
      const orgs = resOrgs.rows.map((o) => ({ label: o.name, value: o }));
      setOrganizationOptions(orgs);
    });
  }, []);

  useEffect(() => {
    if (selectedOrganization !== null) {
      setForm({ ...form, admin_user: null });
      const params = toQueryParams({
        organizationCode: selectedOrganization.value.code,
      });
      getUsers(params).then((resUsers) => {
        const users = resUsers.map((u) => ({ label: u.name, value: u }));
        setUserOptions(users);
      });
    }
  }, [selectedOrganization]);

  return (
    <FormWrapper>
      <BreadCrumb crumbs={crumbs} />
      <PageTitle>PartnerClient登録</PageTitle>

      <form onSubmit={openModal}>
        <FormGrid>
          <Grid.Item size={2} alignItems="center">
            <Label>
              組織+管理ユーザ <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={10} gap="8px">
            <Select
              options={organizationOptions}
              value={selectedOrganization}
              onChange={setSelectedOrganization}
              placeholder="「組織」を選択"
            />
            <Select
              options={userOptions}
              value={form.admin_user}
              onChange={handleChangeForm('admin_user')}
              placeholder="「管理ユーザ」を選択"
            />
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2} alignItems="center">
            <Label>
              PartnerClient名 <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={10}>
            <TextBox
              value={form.name}
              onBlur={handleChangeForm('name', (v) => v.target.value)}
              innerRef={nameField}
            />
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>
              説明 <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={10}>
            <TextArea
              cols="90"
              value={form.description || ''}
              onChange={handleChangeForm('description', (v) => v.currentTarget.value)}
              maxLength={300}
              placeholder="PartnerClientの説明文を記載"
            />
          </Grid.Item>

          <Grid.Divider />
        </FormGrid>
        <Button disabled={!form.name || !form.admin_user}>登録</Button>
      </form>
      <ConfirmModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        title="登録内容の確認"
        form={
          <PartnerClientConfirmModal
            form={form}
            onConfirm={registerPartnerClient}
            onCancel={closeModal}
          />
        }
      />
      {apiResult ? <ApiResults messages={[apiResult]} /> : null}
    </FormWrapper>
  );
};

CreatePartnerClient.propTypes = {
  history: PropTypes.object.isRequired,
};

export default CreatePartnerClient;
